import { useEffect, useState, type ReactNode } from 'react';
import { DollarSign, Store, Package, type LucideIcon } from 'lucide-react';
import { toast } from '@/stores/toast-store';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  useCreateRewardProgram,
  type CreateRewardProgramInput,
  type ProgramType,
} from '@/hooks/customer-engagement/use-reward-programs';

interface Props {
  open: boolean;
  onClose: () => void;
}

// The validity pickers edit wall-clock time in the operator's own browser zone —
// the same semantics as the native datetime-local input they replace. Pin the
// DatePicker to that zone and store the same `YYYY-MM-DDTHH:mm` string the
// backend already receives, so nothing downstream changes.
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
function dateToLocalDatetime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Earning-rule picker cards — icon + one-line description per type, mirroring
// the Customer Type cards in the Create Customer dialog. Order/values are
// unchanged from the old <select>; this drives presentation only.
const PROGRAM_TYPE_META: {
  value: ProgramType;
  label: string;
  description: string;
  icon: LucideIcon;
}[] = [
  { value: 'points_per_dollar', label: 'Per dollar', description: 'Points per $1 spent', icon: DollarSign },
  { value: 'points_per_visit', label: 'Per visit', description: 'Fixed points each visit', icon: Store },
  { value: 'points_per_product', label: 'Per product', description: 'Configured at the register', icon: Package },
];

const EMPTY: CreateRewardProgramInput = {
  name: '',
  programType: 'points_per_dollar',
  pointsPerDollar: 1,
  pointsPerVisit: 10,
  minOrderAmount: 0,
  redemptionRate: 0.05,
  cooldownMinutes: 0,
  locationScope: 'all',
};

export function RewardProgramCreateDialog({ open, onClose }: Props) {
  const [form, setForm] = useState<CreateRewardProgramInput>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const create = useCreateRewardProgram();

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setFieldErrors({});
  }, [open]);

  const handleClose = () => {
    setForm(EMPTY);
    setFieldErrors({});
    onClose();
  };

  const set = <K extends keyof CreateRewardProgramInput>(key: K, value: CreateRewardProgramInput[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldErrors[key as string]) {
      setFieldErrors((e) => {
        const { [key as string]: _, ...rest } = e;
        return rest;
      });
    }
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if ((form.cooldownMinutes ?? 0) < 0) errors.cooldownMinutes = 'Cooldown cannot be negative';
    if ((form.minOrderAmount ?? 0) < 0) errors.minOrderAmount = 'Minimum order cannot be negative';
    if ((form.redemptionRate ?? 0) < 0) errors.redemptionRate = 'Redemption rate cannot be negative';
    if (form.programType === 'points_per_dollar' && (form.pointsPerDollar ?? 0) < 0)
      errors.pointsPerDollar = 'Points per $1 cannot be negative';
    if (form.programType === 'points_per_visit' && (form.pointsPerVisit ?? 0) < 0)
      errors.pointsPerVisit = 'Points per visit cannot be negative';
    if (form.validFrom && form.validTo && new Date(form.validFrom) >= new Date(form.validTo)) {
      errors.validTo = 'Valid to must be after Valid from';
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await create.mutateAsync({
        ...form,
        name: form.name.trim(),
        validFrom: form.validFrom || undefined,
        validTo: form.validTo || undefined,
      });
      toast.success(`Reward program "${form.name.trim()}" created`);
      handleClose();
    } catch (e: any) {
      toast.error(e?.error?.message ?? 'Failed to create reward program');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create Reward Program"
      widthClass="max-w-[680px]"
      footer={
        <div className="flex items-center gap-3">
          <span className="mr-auto hidden text-xs text-muted-foreground sm:block">Name is required.</span>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="reward-program-create-form" disabled={create.isPending}>
            {create.isPending ? 'Creating...' : 'Create Program'}
          </Button>
        </div>
      }
    >
      <form id="reward-program-create-form" onSubmit={handleSubmit} noValidate className="space-y-7">
        {/* ── 1 · basic details (name + earning rule) ──────────────── */}
        <FormSection num={1} title="Basic Details">
          <div>
            <Label htmlFor="reward-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="reward-name"
              className="mt-1"
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Frequent Shopper"
              error={!!fieldErrors.name}
              aria-invalid={!!fieldErrors.name}
            />
            {fieldErrors.name && <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>}
          </div>

          <div>
            <Label>Earning rule</Label>
            <div className="mt-1 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
              {PROGRAM_TYPE_META.map((t) => {
                const Icon = t.icon;
                const selected = form.programType === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => set('programType', t.value)}
                    aria-pressed={selected}
                    className={cn(
                      'flex items-start gap-3 rounded-xl border p-3 text-left transition',
                      selected ? 'border-primary bg-primary/5' : 'border-border bg-white hover:border-primary/40',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        selected ? 'bg-primary text-white' : 'bg-primary/10 text-primary',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-foreground">{t.label}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{t.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {form.programType === 'points_per_dollar' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="reward-points-per-dollar">Points per $1</Label>
                <Input
                  id="reward-points-per-dollar"
                  className="mt-1"
                  type="number"
                  selectOnFocus
                  step="0.0001"
                  min="0"
                  value={form.pointsPerDollar ?? 0}
                  onChange={(e) => set('pointsPerDollar', Number(e.target.value))}
                  error={!!fieldErrors.pointsPerDollar}
                  aria-invalid={!!fieldErrors.pointsPerDollar}
                />
                {fieldErrors.pointsPerDollar && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.pointsPerDollar}</p>
                )}
              </div>
              <div>
                <Label htmlFor="reward-min-order">Minimum order ($) to earn</Label>
                <Input
                  id="reward-min-order"
                  className="mt-1"
                  type="number"
                  selectOnFocus
                  step="0.01"
                  min="0"
                  value={form.minOrderAmount ?? 0}
                  onChange={(e) => set('minOrderAmount', Number(e.target.value))}
                  error={!!fieldErrors.minOrderAmount}
                  aria-invalid={!!fieldErrors.minOrderAmount}
                />
                {fieldErrors.minOrderAmount && (
                  <p className="mt-1 text-xs text-red-500">{fieldErrors.minOrderAmount}</p>
                )}
              </div>
            </div>
          )}

          {form.programType === 'points_per_visit' && (
            <div className="sm:max-w-xs">
              <Label htmlFor="reward-points-per-visit">Points per visit</Label>
              <Input
                id="reward-points-per-visit"
                className="mt-1"
                type="number"
                selectOnFocus
                step="1"
                min="0"
                value={form.pointsPerVisit ?? 0}
                onChange={(e) => set('pointsPerVisit', Number(e.target.value))}
                error={!!fieldErrors.pointsPerVisit}
                aria-invalid={!!fieldErrors.pointsPerVisit}
              />
              {fieldErrors.pointsPerVisit && <p className="mt-1 text-xs text-red-500">{fieldErrors.pointsPerVisit}</p>}
            </div>
          )}
        </FormSection>

        {/* ── 2 · redemption ───────────────────────────────────────── */}
        <FormSection num={2} title="Redemption">
          <div>
            <Label htmlFor="reward-redemption-rate">Redemption rate — dollar value of 1 point</Label>
            <Input
              id="reward-redemption-rate"
              className="mt-1"
              type="number"
              selectOnFocus
              step="0.0001"
              min="0"
              value={form.redemptionRate ?? 0}
              onChange={(e) => set('redemptionRate', Number(e.target.value))}
              error={!!fieldErrors.redemptionRate}
              aria-invalid={!!fieldErrors.redemptionRate}
            />
            {fieldErrors.redemptionRate ? (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.redemptionRate}</p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                e.g. <code className="rounded bg-muted px-1">0.05</code> means 20 points = $1 off.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="reward-cooldown">Cooldown (minutes)</Label>
            <Input
              id="reward-cooldown"
              className="mt-1"
              type="number"
              selectOnFocus
              step="1"
              min="0"
              value={form.cooldownMinutes ?? 0}
              onChange={(e) => set('cooldownMinutes', Number(e.target.value))}
              error={!!fieldErrors.cooldownMinutes}
              aria-invalid={!!fieldErrors.cooldownMinutes}
            />
            {fieldErrors.cooldownMinutes ? (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.cooldownMinutes}</p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                <strong>0 = no restriction (default).</strong> Set a positive value (e.g. <code>240</code> for 4 hours)
                to prevent a customer from repeatedly earning or redeeming within that window.
              </p>
            )}
          </div>
        </FormSection>

        {/* ── 3 · validity window ──────────────────────────────────── */}
        <FormSection num={3} title="Validity window">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="reward-valid-from">Valid from</Label>
              <DatePicker
                mode="datetime"
                portal
                timezone={LOCAL_TZ}
                format="MM/DD/YYYY HH:mm"
                clearable
                className="mt-1"
                placeholder="MM/DD/YYYY HH:mm"
                value={form.validFrom ? new Date(form.validFrom) : null}
                onChange={(v) => set('validFrom', v instanceof Date ? dateToLocalDatetime(v) : undefined)}
                aria-label="Valid from"
              />
            </div>
            <div>
              <Label htmlFor="reward-valid-to">Valid to</Label>
              <DatePicker
                mode="datetime"
                portal
                timezone={LOCAL_TZ}
                format="MM/DD/YYYY HH:mm"
                clearable
                className="mt-1"
                placeholder="MM/DD/YYYY HH:mm"
                value={form.validTo ? new Date(form.validTo) : null}
                onChange={(v) => set('validTo', v instanceof Date ? dateToLocalDatetime(v) : undefined)}
                error={!!fieldErrors.validTo}
                aria-label="Valid to"
              />
              {fieldErrors.validTo && <p className="mt-1 text-xs text-red-500">{fieldErrors.validTo}</p>}
            </div>
          </div>
        </FormSection>
      </form>
    </Modal>
  );
}

// A numbered, titled group of fields — the same visual "spine" used by the
// Create Customer, Create Coupon, and Promotion dialogs, so they all read as
// one design system. Purely presentational — no state, no logic.
function FormSection({ num, title, children }: { num: number; title: string; children: ReactNode }) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/10 text-[11px] font-bold leading-none text-primary">
          {num}
        </span>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
